import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, FailureNotice, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog, type ImpactStatus } from '@/components/platform/ConfirmActionDialog';
import { useEnumLabel, useInstitutionTypeLabel } from '@/components/platform/useLabel';
import { platformApi } from '@/api/platform';
import { Select } from '@/components/ui/Select';
import { useAsyncData } from '@/hooks';
import {
  SUSPENSION_SCOPES,
  institutionDisplayName,
  type InstitutionLifecycle,
  type InstitutionLifecycleEvent,
  type LifecycleAction,
  type LifecycleImpact,
  type PlatformInstitutionRecord,
  type SuspensionScope,
} from '@/types/platform';
import { formatCount, formatDateTime } from '@/utils/format';
import { InstitutionProductsPanel } from './InstitutionProductsPanel';
import { InstitutionAdministratorsPanel } from './InstitutionAdministratorsPanel';
import { InstitutionStructurePanel } from './InstitutionStructurePanel';
import { InstitutionBrandingPanel } from './InstitutionBrandingPanel';
import styles from './platform-pages.module.css';

/**
 * One institution, seen from the platform side: what it is, what it can use, who
 * runs it, and the actions that change its life.
 *
 * THE HEADER'S ENDPOINT DOES NOT EXIST AND THE PAGE NO LONGER DIES WITH IT. Core
 * serves no `GET /platform/institutions/:id`, so the identity block at the top
 * reports honestly that it is missing. Everything else on this page — structure,
 * products, administrators, branding, and the whole of PA-08 — reads from
 * endpoints that are deployed, and they used to be unreachable because they sat
 * inside the failed panel. One missing route must not blank six working ones.
 *
 * THE LIFECYCLE IS ITS OWN STATE MACHINE AND IS ASKED, NOT INFERRED. The actions
 * offered used to be derived from the ONBOARDING state, which belongs to a
 * different entity: that is how the console came to offer RESUME, an action core
 * has no name for, while hiding ENTER_MAINTENANCE and ARCHIVE, which it has always
 * had. The buttons now come from `GET .../lifecycle/actions`, and the action is
 * performed against `POST .../lifecycle/actions` rather than the bare `/lifecycle`
 * path that returned 404 on every attempt.
 *
 * NOBODY SUSPENDS A UNIVERSITY BY CLICKING ONCE. The impact call is a separate
 * request made when the dialog opens, because only the server knows what sits
 * behind an institution id, and the numbers it returns are the ones core actually
 * computes: products losing and retaining access, enabled feature flags, campuses,
 * branches, live integrations. The previous dialog promised an affected-user count
 * and a "data is kept" line that core does not produce, so it rendered a blank next
 * to "users" and an unconditional reassurance next to "data" — both invented.
 */

const DESTRUCTIVE: readonly LifecycleAction[] = ['SUSPEND', 'CLOSE', 'ARCHIVE'];

export function InstitutionDetailPage() {
  const { institutionId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const label = useEnumLabel();
  const typeLabel = useInstitutionTypeLabel();
  const locale = i18n.language;
  const enabled = institutionId.length > 0;

  const detailFetcher = useCallback(() => platformApi.institutions.get(institutionId), [institutionId]);
  const { state: detail, reload: reloadDetail } = useAsyncData<PlatformInstitutionRecord>(
    `institution:${institutionId}`,
    detailFetcher,
    enabled,
  );

  const lifecycleFetcher = useCallback(() => platformApi.institutions.lifecycle(institutionId), [institutionId]);
  const { state: lifecycle, reload: reloadLifecycle } = useAsyncData<InstitutionLifecycle>(
    `institution-lifecycle:${institutionId}`,
    lifecycleFetcher,
    enabled,
  );

  const actionsFetcher = useCallback(
    () => platformApi.institutions.lifecycleActions(institutionId),
    [institutionId],
  );
  const { state: actions, reload: reloadActions } = useAsyncData<LifecycleAction[]>(
    `institution-lifecycle-actions:${institutionId}`,
    actionsFetcher,
    enabled,
  );

  const eventsFetcher = useCallback(
    () => platformApi.institutions.lifecycleEvents(institutionId),
    [institutionId],
  );
  const { state: events, reload: reloadEvents } = useAsyncData<InstitutionLifecycleEvent[]>(
    `institution-lifecycle-events:${institutionId}`,
    eventsFetcher,
    enabled,
  );

  const [action, setAction] = useState<LifecycleAction | null>(null);
  const [impact, setImpact] = useState<LifecycleImpact | null>(null);
  const [impactStatus, setImpactStatus] = useState<ImpactStatus>('loading');
  const [scope, setScope] = useState<SuspensionScope>('FULL_PLATFORM');
  const [productCode, setProductCode] = useState('');
  const [maintenanceEndsAt, setMaintenanceEndsAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const institutionName = detail.status === 'ready' ? institutionDisplayName(detail.data) : institutionId;

  // Asked for when the dialog opens, not when the page loads: it is an expensive
  // question and it must reflect the moment the decision is being made. The scope
  // is part of the question — suspending payments and suspending the whole
  // platform take different things away.
  useEffect(() => {
    if (!action || !enabled) return;
    let active = true;
    setImpactStatus('loading');
    setImpact(null);

    platformApi.institutions
      .lifecycleImpact(
        institutionId,
        action,
        action === 'SUSPEND' ? scope : undefined,
        action === 'SUSPEND' && scope === 'SPECIFIC_PRODUCT' ? productCode || undefined : undefined,
      )
      .then((result) => {
        if (!active) return;
        setImpact(result);
        setImpactStatus('ready');
      })
      .catch(() => {
        // A reporting endpoint being down must not block an emergency suspension.
        // The dialog degrades to an explicit acknowledgement instead of silently
        // pretending the impact is nil.
        if (active) setImpactStatus('unavailable');
      });

    return () => {
      active = false;
    };
  }, [action, enabled, institutionId, scope, productCode]);

  const run = async (reason: string) => {
    if (!action) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.institutions.applyLifecycleAction(institutionId, {
        action,
        reason,
        suspensionScope: action === 'SUSPEND' ? scope : undefined,
        suspendedProductCode: action === 'SUSPEND' && scope === 'SPECIFIC_PRODUCT' ? productCode : undefined,
        maintenanceEndsAt:
          action === 'ENTER_MAINTENANCE' && maintenanceEndsAt
            ? new Date(maintenanceEndsAt).toISOString()
            : undefined,
      });
      // Every one of these can change: the state, which actions are now legal, and
      // the history.
      reloadLifecycle();
      reloadActions();
      reloadEvents();
      setAction(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const scopeIncomplete = action === 'SUSPEND' && scope === 'SPECIFIC_PRODUCT' && productCode.trim().length === 0;

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('platform.institutions.breadcrumbLabel')}>
        <Link to="/admin/institutions">{t('platform.institutions.title')}</Link>
        <Icon name="chevronRight" size={13} />
        <span>{institutionName}</span>
      </nav>

      {/*
        The identity block, and only the identity block, depends on the endpoint
        that does not exist. Everything below it renders regardless.
      */}
      <Panel title={t('platform.institutions.detailsTitle')} icon="institution">
        <AsyncPanel state={detail} onRetry={reloadDetail}>
          {(institution) => (
            <>
              <header className={styles.caseHeader}>
                <div>
                  <h1 className={styles.pageTitle}>{institutionDisplayName(institution)}</h1>
                  <p className={styles.pageLead}>
                    {institution.code} · {typeLabel(institution.institutionType)}
                  </p>
                </div>
                <div className={styles.caseHeaderAside}>
                  <span
                    className={
                      institution.onboardingState === 'ACTIVE'
                        ? styles.okTag
                        : institution.onboardingState === 'SUSPENDED'
                          ? styles.dangerTag
                          : styles.mutedTag
                    }
                  >
                    {/* The onboarding label set covers all thirteen states; the old
                        institutions.status set covered four, so anything mid-pipeline
                        rendered as a raw enum value. */}
                    {label('state.onboarding', institution.onboardingState)}
                  </span>
                </div>
              </header>

              <dl className={styles.detailGrid}>
                {/*
                  ONE name row, not two. The directory resolves display-versus-legal
                  before it sends — `name` is the display name where one is set and the
                  legal name otherwise — so there is no second value to show, and a
                  "Display name: —" row against every institution said only that this
                  screen was asking for something the endpoint does not serve.
                */}
                <dt>{t('platform.institutions.name')}</dt>
                <dd>{institution.name}</dd>
                <dt>{t('platform.institutions.code')}</dt>
                <dd>{institution.code}</dd>
                <dt>{t('platform.institutions.colType')}</dt>
                <dd>
                  {typeLabel(institution.institutionType)}
                  {/*
                    `legacyInstitutionType` is NOT shown, because the directory does not
                    send it. It is a column on platform_institutions, kept so a null type
                    is explicable, and this screen used to read it straight off the row —
                    from an endpoint that has never carried it. Reinstate it by adding it
                    to PlatformInstitutionDetailDto first, not by reading it here.
                  */}
                </dd>
                <dt>{t('platform.institutions.tenant')}</dt>
                <dd>{institution.tenantId || '—'}</dd>
                <dt>{t('platform.institutions.country')}</dt>
                <dd>{institution.countryCode || '—'}</dd>
                <dt>{t('platform.institutions.created')}</dt>
                <dd>{formatDateTime(institution.createdAt, locale)}</dd>
              </dl>
            </>
          )}
        </AsyncPanel>
      </Panel>

      <Panel
        title={t('platform.lifecycle.title')}
        icon="admin"
        emphasis="attention"
        actions={
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => {
              reloadLifecycle();
              reloadActions();
            }}
          >
            <Icon name="refresh" size={15} />
            {t('platform.dashboard.refresh')}
          </button>
        }
      >
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.lifecycle.notice')}
        </p>

        <AsyncPanel state={lifecycle} onRetry={reloadLifecycle}>
          {(current) => (
            <>
              <dl className={styles.detailGrid}>
                <dt>{t('platform.lifecycle.currentState')}</dt>
                <dd>
                  <span className={current.state === 'ACTIVE' ? styles.okTag : styles.warnTag}>
                    {t(`platform.lifecycle.state.${current.state}`, { defaultValue: current.state })}
                  </span>
                </dd>
                <dt>{t('platform.lifecycle.changedAt')}</dt>
                <dd>
                  {formatDateTime(current.stateChangedAt ?? undefined, locale)}
                  {current.stateChangedBy ? ` · ${current.stateChangedBy}` : ''}
                </dd>
                {current.currentReason && (
                  <>
                    <dt>{t('platform.lifecycle.currentReason')}</dt>
                    <dd>{current.currentReason}</dd>
                  </>
                )}
              </dl>

              {current.state === 'SUSPENDED' && (
                <div className={styles.suspensionBanner} role="status">
                  <Icon name="pause" size={18} aria-hidden="true" />
                  <div>
                    <p className={styles.suspensionTitle}>{t('platform.lifecycle.suspendedBannerTitle')}</p>
                    <p className={styles.suspensionBody}>
                      {current.suspensionScope
                        ? t('platform.lifecycle.suspendedBannerScope', {
                            scope: label('suspensionScope', current.suspensionScope),
                          })
                        : t('platform.lifecycle.suspendedBannerNoScope')}
                      {current.suspendedProductCode ? ` — ${current.suspendedProductCode}` : ''}
                      {current.currentReason ? ` — ${current.currentReason}` : ''}
                    </p>
                  </div>
                </div>
              )}

              {current.state === 'MAINTENANCE' && current.maintenanceEndsAt && (
                <p className={styles.rowDetail}>
                  {t('platform.lifecycle.maintenanceUntil', {
                    when: formatDateTime(current.maintenanceEndsAt, locale),
                  })}
                </p>
              )}
            </>
          )}
        </AsyncPanel>

        {/*
          Which actions are legal is the server's answer, and its own request. When
          it cannot be reached the buttons are withheld rather than guessed: a
          button that produces a 409 teaches an operator the rules are arbitrary.
        */}
        {actions.status === 'error' ? (
          <FailureNotice error={actions.error} onRetry={reloadActions} />
        ) : actions.status === 'ready' && actions.data.length === 0 ? (
          <p className={styles.sectionEmpty} role="status">
            {t('platform.lifecycle.noActions')}
          </p>
        ) : actions.status === 'ready' ? (
          <div className={styles.transitionRow}>
            {actions.data.map((candidate) => (
              <button
                key={candidate}
                type="button"
                className={DESTRUCTIVE.includes(candidate) ? styles.dangerBtn : styles.secondaryBtn}
                onClick={() => {
                  setError(null);
                  setScope('FULL_PLATFORM');
                  setProductCode('');
                  setMaintenanceEndsAt('');
                  setAction(candidate);
                }}
              >
                {t(`platform.lifecycle.action.${candidate}`, { defaultValue: candidate })}
              </button>
            ))}
          </div>
        ) : null}
      </Panel>

      <div id="structure">
        <InstitutionStructurePanel institutionId={institutionId} />
      </div>

      <div id="products">
        <InstitutionProductsPanel institutionId={institutionId} />
      </div>

      <div id="administrators">
        <InstitutionAdministratorsPanel institutionId={institutionId} institutionName={institutionName} />
      </div>

      <div id="branding">
        <InstitutionBrandingPanel institutionId={institutionId} />
      </div>

      <Panel title={t('platform.lifecycle.historyTitle')} icon="history">
        <AsyncPanel
          state={events}
          onRetry={reloadEvents}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.lifecycle.noHistory')}
        >
          {(data) => (
            <ul className={styles.timeline}>
              {data.map((entry) => (
                <li key={entry.id} className={styles.timelineItem}>
                  <span
                    className={DESTRUCTIVE.includes(entry.action) ? styles.timelineDotCritical : styles.timelineDot}
                    aria-hidden="true"
                  />
                  <div>
                    <p className={styles.rowTitle}>
                      {t(`platform.lifecycle.action.${entry.action}`, { defaultValue: entry.action })}
                      {' · '}
                      {t(`platform.lifecycle.state.${entry.fromState}`, { defaultValue: entry.fromState })}
                      {' → '}
                      {t(`platform.lifecycle.state.${entry.toState}`, { defaultValue: entry.toState })}
                    </p>
                    <p className={styles.rowMetaLine}>
                      {formatDateTime(entry.occurredAt, locale)}
                      {entry.actorName ? ` · ${entry.actorName}` : ` · ${entry.actorId}`}
                      {entry.suspensionScope ? ` · ${label('suspensionScope', entry.suspensionScope)}` : ''}
                    </p>
                    <p className={styles.rowDetail}>{entry.reason}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </AsyncPanel>
      </Panel>

      <ConfirmActionDialog
        open={action !== null}
        title={t(`platform.lifecycle.confirm.${action ?? 'CLOSE'}.title`, {
          name: institutionName,
          defaultValue: t('platform.lifecycle.confirm.default.title', { name: institutionName }),
        })}
        intro={t(`platform.lifecycle.confirm.${action ?? 'CLOSE'}.intro`, {
          name: institutionName,
          defaultValue: t('platform.lifecycle.confirm.default.intro', { name: institutionName }),
        })}
        tone={action && DESTRUCTIVE.includes(action) ? 'critical' : 'warning'}
        impactStatus={impactStatus}
        impact={impact ? <ImpactSummary impact={impact} locale={locale} /> : null}
        extraFields={
          action === 'SUSPEND' ? (
            <div className={styles.dialogGrid}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('platform.lifecycle.scopeLabel')}</span>
                <Select
                  value={scope}
                  onChange={(value) => setScope(value as SuspensionScope)}
                  options={SUSPENSION_SCOPES.map((value) => ({
                    value,
                    label: label('suspensionScope', value),
                  }))}
                />
                <span className={styles.fieldHint}>{t('platform.lifecycle.scopeHint')}</span>
              </label>
              {scope === 'SPECIFIC_PRODUCT' && (
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('platform.lifecycle.productLabel')}</span>
                  <input
                    className={styles.input}
                    value={productCode}
                    autoComplete="off"
                    onChange={(event) => setProductCode(event.target.value.toUpperCase())}
                  />
                </label>
              )}
            </div>
          ) : action === 'ENTER_MAINTENANCE' ? (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.lifecycle.maintenanceEndsAt')}</span>
              <input
                className={styles.input}
                type="datetime-local"
                value={maintenanceEndsAt}
                onChange={(event) => setMaintenanceEndsAt(event.target.value)}
              />
              {/*
                Optional on the server and worth asking for anyway: a maintenance
                state with no end is one somebody has to remember to leave.
              */}
              <span className={styles.fieldHint}>{t('platform.lifecycle.maintenanceEndsHint')}</span>
            </label>
          ) : undefined
        }
        extraValid={!scopeIncomplete}
        confirmPhrase={action && DESTRUCTIVE.includes(action) ? institutionName : undefined}
        confirmLabel={t(`platform.lifecycle.action.${action ?? 'CLOSE'}`, { defaultValue: action ?? '' })}
        submitting={submitting}
        error={error}
        onCancel={() => setAction(null)}
        onConfirm={run}
      />
    </div>
  );
}

function ImpactSummary({ impact, locale }: { impact: LifecycleImpact; locale: string }) {
  const { t } = useTranslation();

  return (
    <>
      <dl className={styles.impactFacts}>
        <dt>{t('platform.lifecycle.impact.resultingState')}</dt>
        <dd>
          {t(`platform.lifecycle.state.${impact.currentState}`, { defaultValue: impact.currentState })}
          {' → '}
          {t(`platform.lifecycle.state.${impact.resultingState}`, { defaultValue: impact.resultingState })}
        </dd>
        {/*
          Losing and retaining are both listed. "Six products lose access" without
          "four keep it" reads as a total shutdown, and the difference is the whole
          reason a scoped suspension exists.
        */}
        <dt>{t('platform.lifecycle.impact.losing')}</dt>
        <dd>{impact.productsLosingAccess.length > 0 ? impact.productsLosingAccess.join(', ') : '—'}</dd>
        <dt>{t('platform.lifecycle.impact.retaining')}</dt>
        <dd>{impact.productsRetainingAccess.length > 0 ? impact.productsRetainingAccess.join(', ') : '—'}</dd>
        <dt>{t('platform.lifecycle.impact.featureFlags')}</dt>
        <dd>{formatCount(impact.enabledFeatureFlagCount, locale)}</dd>
        <dt>{t('platform.lifecycle.impact.sites')}</dt>
        <dd>
          {t('platform.lifecycle.impact.sitesValue', {
            campuses: formatCount(impact.campusCount, locale),
            branches: formatCount(impact.branchCount, locale),
          })}
        </dd>
        <dt>{t('platform.lifecycle.impact.integrations')}</dt>
        <dd>{formatCount(impact.liveIntegrationCount, locale)}</dd>
      </dl>

      {impact.warnings.length > 0 && (
        <ul className={styles.impactListWarning}>
          {impact.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
    </>
  );
}
